"use client";

import "./index.css";
import { useState, useMemo, useEffect, useRef } from "react";
import useAllLeagues from "@/hooks/useAllLeagues";
import { KbvPluginService } from "@/services/kbvPluginService";
import LeagueFilterV20QuickChips from "./LeagueFilterV20QuickChips";
import LeagueFilterV20Controls from "./LeagueFilterV20Controls";
import LeagueFilterV20Dropdown from "./LeagueFilterV20Dropdown";

const FIELD_BASE =
  "box-border h-[38px] w-full min-w-0 rounded-lg border border-solid border-[var(--theme-primary)] bg-[var(--theme-foreground)] px-2 text-sm text-[var(--theme-text)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--theme-primary)]";
const LIST_SCROLLBAR = "football-scrollbar";
const QUICK_LEAGUE_COUNT = 12;
const CHIP_STRIP =
  "!flex min-w-0 !flex-row !flex-nowrap gap-x-[10px] gap-y-0 !overflow-x-auto overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden [&>*]:!float-none [&>*]:!block rounded-[2px] bg-[linear-gradient(126deg,#183b72,#4482e4)] p-1.5 md:!flex-wrap md:gap-y-[5px] md:!overflow-x-visible";

export default function LeagueFilterV20({
  selectedIds = [],
  onChange,
  className = "",
}) {
  const { leagues, loading } = useAllLeagues();
  const [hotLeagues, setHotLeagues] = useState([]);
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [tempSelectedIds, setTempSelectedIds] = useState(selectedIds || []);
  const [viewMode, setViewMode] = useState("league");
  const rootRef = useRef(null);

  useEffect(() => {
    setTempSelectedIds(selectedIds || []);
  }, [selectedIds]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!open) return;
      const el = rootRef.current;
      if (el && !el.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  useEffect(() => {
    let active = true;

    const fetchHotLeagues = async () => {
      const data = await KbvPluginService.getHotLeagues({
        page: 1,
        limit: QUICK_LEAGUE_COUNT,
        fields: "id,name,logo,slug,nameVN",
      });
      if (!active) return;
      setHotLeagues(Array.isArray(data?.data) ? data.data : data || []);
    };

    fetchHotLeagues();
    return () => {
      active = false;
    };
  }, []);

  const normalizedLeagues = useMemo(
    () =>
      (leagues || []).map((l) => ({
        ...l,
        country: l.country || l.countryName || "Khác",
      })),
    [leagues],
  );

  const filteredLeagues = useMemo(() => {
    if (!keyword.trim()) return normalizedLeagues || [];
    const lower = keyword.toLowerCase();
    return (normalizedLeagues || []).filter((league) => {
      const nameVN = (league.nameVN || "").toLowerCase();
      const name = (league.name || "").toLowerCase();
      const country = (league.country || "").toLowerCase();
      return (
        nameVN.includes(lower) || name.includes(lower) || country.includes(lower)
      );
    });
  }, [normalizedLeagues, keyword]);

  const quickLeagueChips = useMemo(
    () =>
      (hotLeagues?.length
        ? hotLeagues
        : normalizedLeagues.slice(0, QUICK_LEAGUE_COUNT)
      ).map((league) => ({
        ...league,
        id: Number(league.id),
      })),
    [hotLeagues, normalizedLeagues],
  );

  const leaguesByCountry = useMemo(() => {
    const groups = {};
    for (const league of filteredLeagues) {
      const countryKey = league.country || "Khác";
      if (!groups[countryKey]) {
        groups[countryKey] = {
          country: countryKey,
          countryLogo: league.countryLogo || null,
          items: [],
        };
      }
      groups[countryKey].items.push(league);
    }
    return Object.values(groups);
  }, [filteredLeagues]);

  const handleToggleLeague = (id) => {
    setTempSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const handleSelectAll = () => {
    const allIds = (filteredLeagues || []).map((l) => l.id);
    setTempSelectedIds(allIds);
  };

  const handleClearAll = () => {
    setTempSelectedIds([]);
  };

  const handleConfirm = () => {
    onChange?.(tempSelectedIds);
    setOpen(false);
  };

  const hasSelection = selectedIds && selectedIds.length > 0;
  const triggerLabel = hasSelection
    ? `${selectedIds.length} giải đấu`
    : "Tất cả giải đấu";
  const allFilteredSelected =
    filteredLeagues.length > 0 &&
    filteredLeagues.every((l) => tempSelectedIds.includes(l.id));
  const liActiveAll = tempSelectedIds.length === 0;
  const handleQuickChipClick = (leagueId) => {
    const onlyThis =
      selectedIds.length === 1 && String(selectedIds[0]) === String(leagueId);
    if (onlyThis) {
      onChange?.([]);
    } else {
      onChange?.([leagueId]);
    }
  };

  return (
    <div
      ref={rootRef}
      className={`relative w-full min-w-0 text-[var(--theme-text)] ${className}`}
    >
      <LeagueFilterV20QuickChips
        chipStripClassName={CHIP_STRIP}
        quickLeagueChips={quickLeagueChips}
        selectedIds={selectedIds}
        onQuickChipClick={handleQuickChipClick}
      />

      <LeagueFilterV20Controls
        keyword={keyword}
        onKeywordChange={setKeyword}
        onOpen={() => setOpen(true)}
        onToggleOpen={() => setOpen((v) => !v)}
        open={open}
        triggerLabel={triggerLabel}
        fieldBaseClassName={FIELD_BASE}
      />

      <LeagueFilterV20Dropdown
        open={open}
        loading={loading}
        filteredLeagues={filteredLeagues}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        liActiveAll={liActiveAll}
        tempSelectedIds={tempSelectedIds}
        leaguesByCountry={leaguesByCountry}
        allFilteredSelected={allFilteredSelected}
        onClearAll={handleClearAll}
        onToggleLeague={handleToggleLeague}
        onSelectAll={handleSelectAll}
        onConfirm={handleConfirm}
        listScrollbarClassName={LIST_SCROLLBAR}
      />
    </div>
  );
}
